import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../shared/Navbar';
import './LandingPage.css';
import './GCSEPage.css';
import { LearningChatMessage, LearningNav, LearningHero, HowItWorks, LearningFooter } from './LearningPageSections';

const exampleMessages: readonly LearningChatMessage[] = [
  {
    role: 'tutor',
    content: 'An enzyme-controlled reaction speeds up as temperature rises, then slows sharply above the optimum. How would you explain that rise and fall in reaction rate?',
  },
  {
    role: 'student',
    content: 'The particles move faster, so they collide more',
  },
  {
    role: 'tutor',
    content: 'That explains the increase, but why does the reaction rate drop as the temperature increases further?',
  },
];

const subjects = [
  {
    name: 'Mathematics',
    detail: 'Learn • Practise • Prepare',
    className: 'maths',
    path: 'M9 12h12M15 6v12M29 10h10M29 30h10M29 38h10M10 31l10 10M20 31 10 41',
  },
  {
    name: 'Science',
    detail: 'Biology • Chemistry • Physics',
    className: 'science',
    path: 'M19 6v12L9 36a4 4 0 0 0 4 6h22a4 4 0 0 0 4-6L29 18V6M16 29h16M16 6h16',
    circles: true,
  },
  {
    name: 'English Language',
    detail: 'Reading • Writing • Exams',
    className: 'language',
    path: 'M8 10h25a5 5 0 0 1 5 5v13a5 5 0 0 1-5 5H20l-9 7v-7H8a5 5 0 0 1-5-5V15a5 5 0 0 1 5-5zM12 18h17M12 24h13',
  },
  {
    name: 'English Literature',
    detail: 'Texts • Ideas • Essays',
    className: 'literature',
    path: 'M7 8h14a6 6 0 0 1 6 6v27H13a6 6 0 0 1-6-6zM41 8H27v33h8a6 6 0 0 0 6-6zM13 16h8M13 22h8M33 16h3M33 22h3',
  },
];

const browseUrl = (subject: string) =>
  `/browse?level=GCSE&subject=${encodeURIComponent(subject)}`;

export default function GCSEPage() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'GCSE Learning | TeachMeNow';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <div className="lp-root learning-page gcse-page">
      <Navbar />
      <LearningNav />
      <main>
        <LearningHero lessonTitle="GCSE Biology · Enzymes" messages={exampleMessages} title="Get unstuck. Build your understanding."
          description="Ready-made GCSE courses in maths, science and English, with a clear sequence of lessons and practice. Work through prepared teaching content with an AI tutor that explains ideas, asks questions and helps you learn from your answers.">
          <a className="tm-button tm-button--primary" href="#gcse-subjects">Choose your GCSE subject <span aria-hidden="true">→</span></a>
          <a className="tm-button tm-button--secondary" href="#how-it-works">How it works</a>
        </LearningHero>
        <section className="learning-section tm-shell" id="gcse-subjects" aria-labelledby="gcse-subjects-title">
          <div className="learning-section-heading"><h2 id="gcse-subjects-title">Choose your GCSE subject</h2><p>Open a course to check its topics, exam board and tier where listed, then choose a lesson.</p></div>
        <div className="gcse-subject-grid">
          {subjects.map(subject => (
            <Link
              className={`gcse-subject-card gcse-subject-card--${subject.className}`}
              to={browseUrl(subject.name)}
              key={subject.name}
            >
              <span className="gcse-subject-icon" aria-hidden="true">
                <svg viewBox="0 0 48 48">
                  <path d={subject.path} />
                  {subject.circles && (
                    <>
                      <circle cx="22" cy="34" r="1.6" />
                      <circle cx="29" cy="37" r="1.6" />
                    </>
                  )}
                </svg>
              </span>
              <span className="gcse-subject-copy">
                <h3>{subject.name}</h3>
                <span>{subject.detail}</span>
              </span>
              <span className="gcse-subject-arrow" aria-hidden="true">→</span>
            </Link>
          ))}
          </div>
        </section>

        <HowItWorks />
      </main>
      <LearningFooter />
    </div>
  );
}
